import React, { useState } from "react";
import { toast } from "sonner";
import { postInquiry } from "../lib/api";
import { Check } from "lucide-react";

const SERVICES = [
  { value: "hotels", label: "Hotels" },
  { value: "villas", label: "Villas" },
  { value: "private-chef", label: "Private Chef" },
  { value: "babysitting", label: "Babysitting" },
  { value: "private-transfer", label: "Private Transfer" },
  { value: "car-rental", label: "Car Rental" },
  { value: "yacht", label: "Yacht" },
  { value: "jet", label: "Jet" },
  { value: "experiences", label: "Experiences" },
];

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  service: "",
  party_size: "",
  dates: "",
  message: "",
};

export default function ConciergePage() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("A few fields are still empty.");
      return;
    }
    setSubmitting(true);
    try {
      await postInquiry(form);
      setDone(true);
      toast.success("Your note has been received.", { description: "We respond, by hand, within 24 hours." });
      setForm(INITIAL);
    } catch (err) {
      toast.error("We could not deliver your note. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grain" data-testid="concierge-page">
      {/* Hero */}
      <section className="container-ml pt-40 md:pt-48 pb-16 md:pb-24">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-10">
            <p className="overline mb-8">Concierge</p>
            <h1 className="font-serif-ml text-6xl sm:text-7xl md:text-[11rem] leading-[0.82] tracking-[-0.03em] text-[var(--ml-text)]">
              Write to <em className="text-[var(--ml-gold)]">us.</em>
            </h1>
            <p className="font-serif-ml italic text-xl md:text-2xl text-[var(--ml-text-dim)] mt-10 max-w-2xl leading-snug">
              The fastest way to access the addresses we do not publish — a note, a few details, an answer within twenty-four hours.
            </p>
          </div>
        </div>
      </section>

      <div className="rule mb-20 container-ml" />

      {/* Form */}
      <section className="container-ml pb-32 grid grid-cols-12 gap-6 md:gap-16">
        <aside className="col-span-12 md:col-span-4">
          <p className="overline mb-6">What we arrange</p>
          <ul className="space-y-3">
            {SERVICES.map(s => (
              <li key={s.value} className="flex items-center gap-3 text-[var(--ml-text-dim)] text-base">
                <span className="h-px w-6 bg-[var(--ml-gold)]" />
                {s.label}
              </li>
            ))}
          </ul>

          <div className="mt-16">
            <p className="overline mb-4">Or write directly</p>
            <p className="font-serif-ml text-2xl text-[var(--ml-text)]">hola@menorcaluxury.com</p>
          </div>
        </aside>

        <div className="col-span-12 md:col-span-8">
          {done ? (
            <div className="border border-[var(--ml-gold)]/40 p-12 md:p-16 text-center fade-in" data-testid="inquiry-success">
              <div className="inline-flex w-16 h-16 items-center justify-center rounded-full border border-[var(--ml-gold)] text-[var(--ml-gold)] mb-8">
                <Check size={28} />
              </div>
              <h2 className="font-serif-ml text-4xl md:text-6xl tracking-tight text-[var(--ml-text)] mb-6">
                Your note is on its way.
              </h2>
              <p className="font-serif-ml italic text-xl text-[var(--ml-text-dim)] max-w-xl mx-auto">
                We respond, by hand, within twenty-four hours. Often sooner.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="inquiry-reset-btn"
                className="btn-ml-ghost mt-12"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="inquiry-form" className="space-y-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
                <div>
                  <label className="overline mb-3 block">Name</label>
                  <input data-testid="inquiry-name" type="text" required value={form.name} onChange={update("name")} className="input-ml" placeholder="Your full name" />
                </div>
                <div>
                  <label className="overline mb-3 block">Email</label>
                  <input data-testid="inquiry-email" type="email" required value={form.email} onChange={update("email")} className="input-ml" placeholder="your@email.com" />
                </div>
                <div>
                  <label className="overline mb-3 block">Phone (optional)</label>
                  <input data-testid="inquiry-phone" type="tel" value={form.phone} onChange={update("phone")} className="input-ml" placeholder="+44 ··· ··· ····" />
                </div>
                <div>
                  <label className="overline mb-3 block">Service</label>
                  <select data-testid="inquiry-service" value={form.service} onChange={update("service")} className="input-ml appearance-none cursor-pointer">
                    <option value="">Select a service</option>
                    {SERVICES.map(s => (
                      <option key={s.value} value={s.value} className="bg-[var(--ml-bg)]">{s.label}</option>
                    ))}
                    <option value="multiple" className="bg-[var(--ml-bg)]">A full itinerary</option>
                  </select>
                </div>
                <div>
                  <label className="overline mb-3 block">Party size</label>
                  <input data-testid="inquiry-party" type="text" value={form.party_size} onChange={update("party_size")} className="input-ml" placeholder="e.g. 4 adults, 2 children" />
                </div>
                <div>
                  <label className="overline mb-3 block">Approximate dates</label>
                  <input data-testid="inquiry-dates" type="text" value={form.dates} onChange={update("dates")} className="input-ml" placeholder="e.g. 12–19 July" />
                </div>
              </div>

              <div>
                <label className="overline mb-3 block">Your note</label>
                <textarea
                  data-testid="inquiry-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className="input-ml resize-none"
                  placeholder="Tell us what you would like. The more we know, the better we can answer."
                />
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4">
                <p className="text-xs tracking-[0.18em] uppercase text-[var(--ml-text-muted)] max-w-md">
                  Submitted in confidence. We share your details with no third party.
                </p>
                <button
                  type="submit"
                  data-testid="inquiry-submit"
                  disabled={submitting}
                  className="btn-ml-gold disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Send the note"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
